import {
  Box,
  Button,
  Card,
  Checkbox,
  Flex,
  Heading,
  HStack,
  InfoBox,
  Stack,
  Text,
  VStack,
  notify,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdArrowBack, MdDownload, MdUploadFile } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
  CustomerImportCommitResult,
  CustomerImportEligibleServer,
  CustomerImportLimits,
  CustomerImportPreviewResult,
  customerImportService,
} from "services/customerImportService";
import { getThemeColor } from "theme/colors";
import { formatFileSize } from "utils/formatFileSize";
import { RowsTable, Summary } from "./components/ImportResults";
import {
  commitInParts,
  DEFAULT_IMPORT_LIMITS,
  ImportPlan,
  planImport,
  previewInParts,
} from "./importParts";

const errorText = (err: any, fallback: string): string =>
  err?.response?.data?.error ?? fallback;

const CustomerImport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CustomerImportPreviewResult | null>(
    null,
  );
  const [commit, setCommit] = useState<CustomerImportCommitResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-upload caps from the API. A file over them is split into parts, only
  // after the admin agrees, and each part is previewed and committed in turn.
  const [limits, setLimits] = useState<CustomerImportLimits>(
    DEFAULT_IMPORT_LIMITS,
  );
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [splitAccepted, setSplitAccepted] = useState(false);
  const [partInProgress, setPartInProgress] = useState<number | null>(null);
  const [partialCommit, setPartialCommit] = useState<{
    committed: number;
    total: number;
  } | null>(null);
  const [updateExisting, setUpdateExisting] = useState(false);

  // Servers the tenant can pre-fill credentials for. Empty list means the
  // tenant has no widget→server mappings yet — in that case the picker UI
  // collapses and the basic template is the only thing on offer.
  const [eligibleServers, setEligibleServers] = useState<
    CustomerImportEligibleServer[]
  >([]);
  const [selectedServerIds, setSelectedServerIds] = useState<Set<string>>(
    new Set(),
  );
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  useEffect(() => {
    let cancelled = false;
    customerImportService
      .listEligibleServers()
      .then((rows) => {
        if (!cancelled) setEligibleServers(rows);
      })
      .catch(() => {
        // Best-effort: a failure here just collapses the picker, the basic
        // template still downloads via the same endpoint with empty serverIds.
      });
    customerImportService
      .getLimits()
      .then((value) => {
        if (!cancelled) setLimits(value);
      })
      .catch(() => {
        // The defaults match the API's caps.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPlan(null);
    setSplitAccepted(false);
    if (!file) return;
    let cancelled = false;
    planImport(file, limits)
      .then((next) => {
        if (!cancelled) setPlan(next);
      })
      .catch(() => {
        if (!cancelled) setPlan({ rowCount: 0, parts: [{ file }] });
      });
    return () => {
      cancelled = true;
    };
  }, [file, limits]);

  const partCount = plan?.parts.length ?? 0;
  const needsSplit = partCount > 1;
  const readyToPreview = !!plan && (!needsSplit || splitAccepted);

  const resetResults = () => {
    setPreview(null);
    setCommit(null);
    setPartialCommit(null);
    setError(null);
  };

  const toggleServer = (id: string) => {
    setSelectedServerIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleUpdateExisting = () => {
    if (busy) return;
    setUpdateExisting((prev) => !prev);
    resetResults();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    resetResults();
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      await customerImportService.downloadTemplate(
        Array.from(selectedServerIds),
      );
    } catch {
      // Universal error toast already fired by axios response interceptor.
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handlePreview = async () => {
    if (!plan) return;
    setBusy(true);
    resetResults();
    try {
      const result = await previewInParts(
        plan.parts,
        (part) => customerImportService.preview(part, updateExisting),
        setPartInProgress,
        t("app.customerImport.parts.duplicateAcrossParts", {
          defaultValue: "Duplicate CustomerId within this file.",
        }),
      );
      setPreview(result);
    } catch (err: any) {
      setError(
        errorText(
          err,
          t("app.customerImport.previewFailed", {
            defaultValue: "Preview failed.",
          }),
        ),
      );
    } finally {
      setBusy(false);
      setPartInProgress(null);
    }
  };

  const handleCommit = async () => {
    if (!plan || !preview || preview.errors > 0) return;
    setBusy(true);
    setError(null);
    const commitFailed = t("app.customerImport.commitFailed", {
      defaultValue: "Commit failed.",
    });
    try {
      const {
        result,
        committedParts,
        error: failure,
      } = await commitInParts(
        plan.parts,
        (part) => customerImportService.commit(part, updateExisting),
        setPartInProgress,
      );
      if (failure && committedParts === 0) {
        setError(errorText(failure, commitFailed));
        return;
      }
      const stopped = committedParts < plan.parts.length;
      setCommit(result);
      setPartialCommit(
        stopped && committedParts > 0
          ? { committed: committedParts, total: plan.parts.length }
          : null,
      );
      if (failure) setError(errorText(failure, commitFailed));
      notify({
        type: stopped ? "error" : "success",
        title:
          stopped && committedParts > 0
            ? t("app.customerImport.parts.stopped", {
                defaultValue: "Import stopped part-way",
              })
            : stopped
              ? t("app.customerImport.committedWithErrors", {
                  defaultValue: "Commit rolled back due to errors.",
                })
              : t("app.customerImport.committed", {
                  defaultValue: "Import committed.",
                }),
      });
    } catch (err: any) {
      setError(errorText(err, commitFailed));
    } finally {
      setBusy(false);
      setPartInProgress(null);
    }
  };

  return (
    <Stack gap={4} w="full" maxW="1100px">
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <HStack>
          <Button intent="ghost" onClick={() => navigate("/admin/customers")}>
            <MdArrowBack /> {t("app.common.back", { defaultValue: "Back" })}
          </Button>
          <Heading>
            {t("app.customerImport.title", {
              defaultValue: "Import customers from CSV",
            })}
          </Heading>
        </HStack>
      </Flex>

      <Card.Root
        borderWidth="1px"
        borderColor={getThemeColor("card.border")}
        borderRadius="md"
      >
        <Card.Body>
          <Stack gap={3}>
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {t("app.customerImport.instructions", {
                defaultValue:
                  "CSV columns: CustomerId, CustomerEmail, CustomerCompanyId, CustomerCompanyName, Pin, Password, Role. At most one of Pin / Password per row. Role defaults to Admin.",
              })}
            </Text>
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {t("app.customerImport.limits", {
                rows: limits.maxRows,
                size: formatFileSize(limits.maxFileBytes),
                defaultValue:
                  "Each upload takes up to {{rows}} customers and {{size}}. A larger file can be uploaded in parts.",
              })}
            </Text>

            {eligibleServers.length > 0 && (
              <Box
                borderWidth="1px"
                borderColor="var(--border-subtle)"
                borderRadius="md"
                p={3}
              >
                <Stack gap={2}>
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold">
                      {t("app.customerImport.serverPrefill.title", {
                        defaultValue: "Pre-fill server credentials (optional)",
                      })}
                    </Text>
                    <Text
                      fontSize="xs"
                      color="var(--text-secondary-color)"
                      mt={0.5}
                    >
                      {t("app.customerImport.serverPrefill.intro", {
                        defaultValue:
                          "Select the servers you want to ship credentials for. The downloaded template will include an example ServerAuthorizationsJson cell shaped to each selected server's auth headers — replace the placeholders with real values before importing.",
                      })}
                    </Text>
                  </Box>
                  <VStack align="stretch" gap={1.5}>
                    {eligibleServers.map((server) => (
                      <HStack
                        key={server.id}
                        gap={3}
                        align="center"
                        cursor="pointer"
                        onClick={() => toggleServer(server.id)}
                        py={1}
                      >
                        <Checkbox
                          checked={selectedServerIds.has(server.id)}
                          onCheckedChange={() => toggleServer(server.id)}
                          pointerEvents="none"
                        />
                        <Box flex="1">
                          <Text fontSize="sm" fontWeight="medium">
                            {server.name}
                          </Text>
                          <Text
                            fontSize="xs"
                            color="var(--text-secondary-color)"
                          >
                            {server.authorizationType}
                            {server.headerNames.length > 0 &&
                              ` — ${t(
                                "app.customerImport.serverPrefill.headers",
                                { defaultValue: "headers" },
                              )}: ${server.headerNames.join(", ")}`}
                          </Text>
                        </Box>
                      </HStack>
                    ))}
                  </VStack>
                </Stack>
              </Box>
            )}

            <HStack
              gap={3}
              align="flex-start"
              cursor={busy ? "default" : "pointer"}
              onClick={toggleUpdateExisting}
            >
              <Checkbox
                checked={updateExisting}
                onCheckedChange={toggleUpdateExisting}
                pointerEvents="none"
                disabled={busy}
                mt={0.5}
              />
              <Box flex="1">
                <Text fontSize="sm" fontWeight="medium">
                  {t("app.customerImport.updateExisting.label", {
                    defaultValue: "Update existing customers",
                  })}
                </Text>
                <Text fontSize="xs" color="var(--text-secondary-color)">
                  {t("app.customerImport.updateExisting.hint", {
                    defaultValue:
                      "Off: customers that already exist are skipped and left unchanged. On: their email, company and role are overwritten, and a PIN or password in the file replaces theirs, clears a sign-in lockout and signs them out.",
                  })}
                </Text>
              </Box>
            </HStack>

            <HStack gap={3}>
              <Box flex="1">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileSelected}
                  disabled={busy}
                  style={{ width: "100%" }}
                />
                {file && (
                  <Text
                    fontSize="xs"
                    color="var(--text-secondary-color)"
                    mt={1}
                  >
                    {file.name} ({Math.round(file.size / 1024)} KB)
                  </Text>
                )}
              </Box>
              <Button
                intent="secondary"
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate}
              >
                <MdDownload />{" "}
                {t("app.customerImport.downloadTemplate", {
                  defaultValue: "Download template",
                })}
              </Button>
              <Button
                intent="primary"
                onClick={handlePreview}
                disabled={!file || busy || !readyToPreview}
              >
                <MdUploadFile />{" "}
                {t("app.customerImport.preview", { defaultValue: "Preview" })}
              </Button>
            </HStack>

            {file && plan && needsSplit && (
              <InfoBox variant="warning">
                <Stack gap={2}>
                  <Text fontSize="sm">
                    {t("app.customerImport.parts.tooLarge", {
                      rows: plan.rowCount,
                      size: formatFileSize(file.size),
                      maxRows: limits.maxRows,
                      maxSize: formatFileSize(limits.maxFileBytes),
                      defaultValue:
                        "This file has {{rows}} customers ({{size}}). One upload takes up to {{maxRows}} customers and {{maxSize}}.",
                    })}
                  </Text>
                  {splitAccepted ? (
                    <Text fontSize="sm">
                      {t("app.customerImport.parts.splitAccepted", {
                        parts: partCount,
                        defaultValue:
                          "The file will be checked and imported in {{parts}} parts, one after another. If a part fails, the parts before it stay imported and the rest are not sent.",
                      })}
                    </Text>
                  ) : (
                    <HStack>
                      <Button
                        intent="secondary"
                        onClick={() => setSplitAccepted(true)}
                        disabled={busy}
                      >
                        {t("app.customerImport.parts.split", {
                          parts: partCount,
                          defaultValue: "Upload in {{parts}} parts",
                        })}
                      </Button>
                    </HStack>
                  )}
                </Stack>
              </InfoBox>
            )}
          </Stack>
        </Card.Body>
      </Card.Root>

      {partInProgress !== null && needsSplit && (
        <Text fontSize="sm" color="var(--text-secondary-color)">
          {t("app.customerImport.parts.progress", {
            current: partInProgress,
            total: partCount,
            defaultValue: "Sending part {{current}} of {{total}}…",
          })}
        </Text>
      )}

      {error && (
        <InfoBox variant="error">
          <Text color="red.700">{error}</Text>
        </InfoBox>
      )}

      {preview && !commit && (
        <Card.Root
          borderWidth="1px"
          borderColor={getThemeColor("card.border")}
          borderRadius="md"
        >
          <Card.Body>
            <Stack gap={4}>
              <Heading size="sm">
                {t("app.customerImport.previewResults", {
                  defaultValue: "Preview results",
                })}
              </Heading>

              <HStack gap={4}>
                <Summary
                  label={t("app.customerImport.summary.total", {
                    defaultValue: "Total rows",
                  })}
                  value={preview.totalRows}
                />
                <Summary
                  label={t("app.customerImport.summary.creates", {
                    defaultValue: "New",
                  })}
                  value={preview.creates}
                  intent="success"
                />
                <Summary
                  label={t("app.customerImport.summary.updates", {
                    defaultValue: "Updates",
                  })}
                  value={preview.updates}
                  intent="info"
                />
                {preview.skips > 0 && (
                  <Summary
                    label={t("app.customerImport.summary.skips", {
                      defaultValue: "Skipped",
                    })}
                    value={preview.skips}
                  />
                )}
                <Summary
                  label={t("app.customerImport.summary.errors", {
                    defaultValue: "Errors",
                  })}
                  value={preview.errors}
                  intent={preview.errors > 0 ? "error" : undefined}
                />
              </HStack>

              {preview.newCustomerCompanies.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={1}>
                    {t("app.customerImport.newCompanies", {
                      defaultValue: "CustomerCompanies that will be created:",
                    })}
                  </Text>
                  <Text fontSize="sm">
                    {preview.newCustomerCompanies.join(", ")}
                  </Text>
                </Box>
              )}

              <RowsTable rows={preview.rows} />

              <HStack justify="flex-end">
                <Button
                  intent="primary"
                  onClick={handleCommit}
                  disabled={busy || preview.errors > 0}
                >
                  {busy
                    ? t("app.customerImport.committing", {
                        defaultValue: "Committing…",
                      })
                    : t("app.customerImport.commit", {
                        defaultValue: "Commit import",
                      })}
                </Button>
              </HStack>
            </Stack>
          </Card.Body>
        </Card.Root>
      )}

      {commit && (
        <Card.Root
          borderWidth="1px"
          borderColor={getThemeColor("card.border")}
          borderRadius="md"
        >
          <Card.Body>
            <Stack gap={4}>
              <Heading size="sm">
                {partialCommit
                  ? t("app.customerImport.parts.stopped", {
                      defaultValue: "Import stopped part-way",
                    })
                  : commit.errored > 0
                    ? t("app.customerImport.commitRolledBack", {
                        defaultValue:
                          "Commit rolled back — fix errors and try again",
                      })
                    : t("app.customerImport.commitResults", {
                        defaultValue: "Commit results",
                      })}
              </Heading>
              {partialCommit && (
                <InfoBox variant="warning">
                  <Text fontSize="sm">
                    {t("app.customerImport.parts.partialCommit", {
                      committed: partialCommit.committed,
                      total: partialCommit.total,
                      failed: partialCommit.committed + 1,
                      defaultValue:
                        "{{committed}} of {{total}} parts were imported. Part {{failed}} was not imported and the parts after it were not sent. Fix the problem and import the same file again: with “Update existing customers” off, customers already imported are skipped.",
                    })}
                  </Text>
                </InfoBox>
              )}
              <HStack gap={4}>
                <Summary
                  label={t("app.customerImport.summary.created", {
                    defaultValue: "Created",
                  })}
                  value={commit.created}
                  intent="success"
                />
                <Summary
                  label={t("app.customerImport.summary.updated", {
                    defaultValue: "Updated",
                  })}
                  value={commit.updated}
                  intent="info"
                />
                {commit.skipped > 0 && (
                  <Summary
                    label={t("app.customerImport.summary.skipped", {
                      defaultValue: "Skipped",
                    })}
                    value={commit.skipped}
                  />
                )}
                <Summary
                  label={t("app.customerImport.summary.errors", {
                    defaultValue: "Errors",
                  })}
                  value={commit.errored}
                  intent={commit.errored > 0 ? "error" : undefined}
                />
              </HStack>
              <RowsTable rows={commit.rows} />
            </Stack>
          </Card.Body>
        </Card.Root>
      )}
    </Stack>
  );
};

export default CustomerImport;
